/**
 * Region / currency selector popup
 * Shown on first visit unless dismissed, or via the header/menu open buttons.
 */
class RegionSelectorPopup {
    static CLOSED_KEY = 'regionSelectorPopupClosed';

    constructor() {
        this.popup = document.getElementById('regionselect-popup');
        this.closeButton = document.getElementById('close-regionselect-popup');
        this.openButtons = document.querySelectorAll('#open-regionselector-popup, #open-regionselector-popup-menu');
        this.menu = document.querySelector('.vlisco-menu__wrapper');

        this.bindEvents();
    }

    bindEvents() {
        if (this.closeButton) {
            this.closeButton.addEventListener('click', () => this.close());
        }

        this.openButtons.forEach((button) => button.addEventListener('click', () => this.show()));

        document.addEventListener('keydown', (event) => {
            if (event.code === 'Escape') this.close();
        });

        // Reload after a currency switch so prices re-render in the new currency.
        document.querySelectorAll('.mws-currency_label').forEach((element) => {
            element.addEventListener('click', () => setTimeout(() => location.reload(), 500));
        });
    }

    get dismissed() {
        return !!localStorage.getItem(RegionSelectorPopup.CLOSED_KEY);
    }

    show() {
        if (this.menu) this.menu.classList.remove('active');
        if (this.popup) this.popup.style.display = 'block';
    }

    close() {
        if (this.popup) this.popup.style.display = 'none';
        localStorage.setItem(RegionSelectorPopup.CLOSED_KEY, 'true');
    }
}

/**
 * On first visit, detects the visitor's country via Shopify's browsing-context
 * suggestions and, if that country has its own local store, offers to redirect.
 * The choice (stay / switch) is persisted per store in localStorage; the switch
 * is handed off to the target store through a `vlisco_switch` URL param.
 */
class CountryRedirect {
    static CHOICE_KEY = 'vlisco_country_choice';
    static SELECTED_STORE_KEY = 'vlisco_selected_store';

    constructor() {
        this.params = new URLSearchParams(window.location.search);
        this.menu = document.querySelector('.vlisco-menu__wrapper');
        this.popup = document.getElementById('country-redirect-popup');
        this.locationsElement = document.getElementById('vlisco-stores');
        this.storesElement = document.getElementById('vlisco-country-stores');
        this.testCountry = (this.params.get('vlisco_test_country') || '').toUpperCase();

        if (this.testCountry) {
            this.params.delete('vlisco_test_country');

            const query = this.params.toString();
            const cleanUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;

            window.history.replaceState({}, '', cleanUrl);
        }

        this.handleHandoff();
    }

    static normalizeHost(host) {
        return String(host).replace(/^www\./, '').toLowerCase();
    }

    static currentHost() {
        return CountryRedirect.normalizeHost(window.location.host);
    }

    static hostOf(url) {
        try {
            return CountryRedirect.normalizeHost(new URL(url).host);
        } catch (error) {
            return '';
        }
    }

    get stores() {
        if (!this.storesElement) return [];

        try {
            return JSON.parse(this.storesElement.textContent) || [];
        } catch (error) {
            return [];
        }
    }

    get locations() {
        if (!this.locationsElement) return [];

        try {
            return JSON.parse(this.locationsElement.textContent) || [];
        } catch (error) {
            return [];
        }
    }

    locationsForIso(iso) {
        const target = String(iso).toUpperCase();

        return this.locations.filter(location => String(location.iso).toUpperCase() === target);
    }

    storeTitle(id) {
        const target = String(id).replace(/\D/g, '');
        const match = this.locations.find(location => String(location.id).replace(/\D/g, '') === target);

        return (match && match.name) || '';
    }

    get cartCount() {
        return this.popup ? parseInt(this.popup.dataset.cartCount || '0', 10) : 0;
    }

    getChoice() {
        try {
            return JSON.parse(localStorage.getItem(CountryRedirect.CHOICE_KEY)) || null;
        } catch (error) {
            return null;
        }
    }

    setChoice(iso, action) {
        localStorage.setItem(CountryRedirect.CHOICE_KEY, JSON.stringify({ iso, action, ts: Date.now() }));
    }

    /** Arrived from another store: mark the choice, suppress prompts, clean the URL. */
    handleHandoff() {
        const switchedIso = this.params.get('vlisco_switch');

        if (!switchedIso) return;

        this.setChoice(switchedIso.toUpperCase(), 'switch');
        localStorage.setItem(RegionSelectorPopup.CLOSED_KEY, 'true');

        const storeCode = this.params.get('vlisco_store');

        this.params.delete('vlisco_switch');
        this.params.delete('vlisco_store');

        const query = this.params.toString();
        const cleanUrl = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;

        if (storeCode) {
            localStorage.setItem(CountryRedirect.SELECTED_STORE_KEY, storeCode);
            // Persist the store (id + name) to the cart, then reload the clean URL so listings/
            // sliders on the landing page are filtered from the very first server render.
            fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({
                    attributes: {
                        selected_store: storeCode,
                        selected_store_title: this.storeTitle(storeCode)
                    }
                })
            })
                .then(() => window.location.replace(cleanUrl))
                .catch((error) => {
                    console.error('Could not persist selected store before redirect', error);
                    window.location.replace(cleanUrl);
                });

            return;
        }

        window.history.replaceState({}, '', cleanUrl);
    }

    findStoreForIso(iso) {
        const target = String(iso).toUpperCase();

        return this.stores.find(store => String(store.iso).toUpperCase() === target);
    }

    fillCopy(name, isMultistore) {
        const withCountry = value => String(value || '').replace(/\[country\]/g, name);
        const template = element => (isMultistore && element.dataset.multistoreTemplate) || element.dataset.template || '';

        const body = this.popup.querySelector('[data-redirect-body]');
        const title = this.popup.querySelector('#country-redirect-title');
        const cartNote = this.popup.querySelector('[data-redirect-cart-note]');
        const switchButton = this.popup.querySelector('#country-redirect-switch');

        if (body) body.innerHTML = withCountry(template(body));
        if (title) title.textContent = withCountry(template(title));

        if (switchButton && switchButton.dataset.template) {
            switchButton.textContent = withCountry(switchButton.dataset.template);
        }

        if (cartNote) {
            if (this.cartCount > 0 && cartNote.dataset.template) {
                cartNote.innerHTML = withCountry(cartNote.dataset.template);
                cartNote.style.display = '';
            } else {
                cartNote.style.display = 'none';
            }
        }
    }

    populateStores(iso) {
        const field = this.popup.querySelector('[data-redirect-store-field]');
        const select = this.popup.querySelector('#country-redirect-store');

        if (!field || !select) return;

        const locations = this.locationsForIso(iso);

        if (locations.length === 0) {
            field.style.display = 'none';

            return;
        }

        select.replaceChildren(
            ...locations.map((location) => {
                const option = document.createElement('option');

                option.value = location.id || '';
                option.textContent = location.name || location.id || '';
                option.selected = Boolean(location.default);

                return option;
            })
        );

        field.style.display = '';
    }

    selectedStoreCode(store) {
        if (!store.multistore) return '';

        const select = this.popup.querySelector('#country-redirect-store');

        return (select && select.value) || '';
    }

    redirectTo(store, storeCode) {
        const target = new URL(store.url);

        target.searchParams.set('vlisco_switch', store.iso);

        if (storeCode) target.searchParams.set('vlisco_store', storeCode);

        window.location.href = target.toString();
    }

    markDecided(iso, action) {
        this.setChoice(iso, action);
        localStorage.setItem(RegionSelectorPopup.CLOSED_KEY, 'true');
    }

    switchTo(store) {
        this.markDecided(store.iso, 'switch');

        const storeCode = this.selectedStoreCode(store);
        const shouldClearCart = this.popup.dataset.clearCart === 'true' && this.cartCount > 0;

        if (shouldClearCart) {
            fetch('/cart/clear.js', { method: 'POST' }).finally(() => this.redirectTo(store, storeCode));
        } else {
            this.redirectTo(store, storeCode);
        }
    }

    stayOn(store) {
        this.markDecided(store.iso, 'stay');
        this.popup.style.display = 'none';
        resolveCountryRedirect();
    }

    open(store) {
        this.fillCopy(store.name || store.iso, Boolean(store.multistore));

        if (store.multistore) {
            this.populateStores(store.iso);
        } else {
            const field = this.popup.querySelector('[data-redirect-store-field]');

            if (field) field.style.display = 'none';
        }

        const stayButton = this.popup.querySelector('#country-redirect-stay');
        const closeButton = this.popup.querySelector('#country-redirect-close');
        const switchButton = this.popup.querySelector('#country-redirect-switch');

        if (this.menu) this.menu.classList.remove('active');

        this.popup.style.display = 'block';

        if (stayButton) stayButton.addEventListener('click', () => this.stayOn(store), { once: true });
        if (closeButton) closeButton.addEventListener('click', () => this.stayOn(store), { once: true });
        if (switchButton) switchButton.addEventListener('click', () => this.switchTo(store), { once: true });

        return true;
    }

    async maybeShow() {
        if (!this.popup || this.stores.length === 0) return false;

        try {
            let iso = this.testCountry;

            if (!iso) {
                const response = await fetch('/browsing_context_suggestions.json');
                const data = await response.json();
                const detected = data && data.detected_values && data.detected_values.country;

                iso = detected && (detected.handle || detected.iso_code);
            }

            if (!iso) return false;

            const store = this.findStoreForIso(iso);

            if (!store || !store.url) return false;

            if (CountryRedirect.hostOf(store.url) === CountryRedirect.currentHost()) return false;

            if (!this.testCountry) {
                const choice = this.getChoice();

                if (choice && choice.iso === String(iso).toUpperCase()) return false;
            }

            return this.open(store);
        } catch (error) {
            return false;
        }
    }
}

/**
 * Signals that the country-redirect flow no longer needs to block the page
 */
function resolveCountryRedirect() {
    window.vliscoCountryRedirectResolved = true;
    document.dispatchEvent(new CustomEvent('country-redirect:resolved'));
}

function initLangSelect() {
    const regionPopup = new RegionSelectorPopup();
    const countryRedirect = new CountryRedirect();
    const params = new URLSearchParams(window.location.search);

    // Capture phase runs before localization-form's own handlers, so it works both for the
    // popup dropdown and the footer links.
    document.addEventListener('click', (event) => {
        const item = event.target.closest('[data-value]');

        if (!item) return;

        const form = item.closest('localization-form');

        if (!form) return;

        if (item.closest('[data-target="language_code"]')) return;

        if (!form.querySelector('input[name="country_code"]')) return;

        const store = countryRedirect.findStoreForIso(item.dataset.value);

        if (!store || !store.url) return;

        if (CountryRedirect.hostOf(store.url) === CountryRedirect.currentHost()) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        regionPopup.close();
        countryRedirect.open(store);
    }, true);

    // First-visit decision: the redirect prompt takes priority over the region/currency popup.
    if (params.get('preview_regionSelector') === 'true') {
        regionPopup.show();
        resolveCountryRedirect();
    } else if (countryRedirect.testCountry || !regionPopup.dismissed) {
        countryRedirect.maybeShow().then((shown) => {
            // If the redirect prompt is shown, resolution happens when the user stays/switches.
            if (!shown) {
                regionPopup.show();
                resolveCountryRedirect();
            }
        });
    } else {
        resolveCountryRedirect();
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLangSelect);
} else {
    initLangSelect();
}
